/**
 * @fileoverview Notification Context for managing application-wide notifications and user feedback.
 *
 * This context provides comprehensive notification functionality including:
 * - Multiple notification systems (custom, notistack, notiflix)
 * - Success, error, info, and choice notifications
 * - Loading spinners with completion tracking
 * - Custom notification components with animations
 * - Choice modals for user confirmations
 * - Enhanced API response handling
 * - Notification persistence and auto-dismissal
 * - Custom notification styling and theming
 *
 * Used throughout the application for:
 * - API response feedback (success/error messages)
 * - Loading states during async operations
 * - User confirmation dialogs
 * - System status notifications
 * - Form validation feedback
 * - Progress indicators
 */

import { useSnackbar } from 'notistack';
import { createContext, useContext, useState, useRef, useEffect, useCallback, useMemo } from 'react';
import Notiflix from "notiflix";
import { PulseLoader } from 'react-spinners';
import '../styles/CustomNotifications.css';
import ChoiceModal from '../components/ChoiceModal';
import {
  FiX,
  FiCheck,
  FiAlertTriangle,
  FiInfo,
  FiLoader,
  FiHelpCircle
} from 'react-icons/fi';

/**
 * React Context for notification state and methods
 * @type {React.Context<NotificationContextValue>}
 */
const NotifyContext = createContext();

/**
 * Custom hook to access the notification context.
 *
 * @function useNotify
 * @returns {NotificationContextValue} The notification context value containing notification methods
 *
 * @example
 * ```jsx
 * function MyComponent() {
 *   const { success, error, spinner, choice } = useNotify();
 *
 *   const handleSave = async () => {
 *     const loader = spinner('Saving...');
 *     try {
 *       await saveData();
 *       loader.complete(true, 'Saved successfully!');
 *     } catch (err) {
 *       loader.complete(false, 'Save failed');
 *     }
 *   };
 *
 *   const handleDelete = () => {
 *     choice('Are you sure you want to delete?',
 *       () => deleteItem(),
 *       () => console.log('Cancelled')
 *     );
 *   };
 * }
 * ```
 */
export const useNotify = () => {
  return useContext(NotifyContext);
};

/**
 * Individual notification component with animations and progress tracking.
 * Memoized to prevent unnecessary rerenders.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.notification - The notification object
 * @param {string} props.notification.id - Unique notification ID
 * @param {string} props.notification.type - Notification type (success, error, info, loading, choice)
 * @param {string} props.notification.message - Notification message
 * @param {string} [props.notification.title] - Optional notification title
 * @param {React.ReactNode} props.notification.icon - Icon component
 * @param {boolean} [props.notification.persist] - Whether notification persists
 * @param {number} [props.notification.duration] - Auto-dismiss duration in ms
 * @param {boolean} [props.notification.important] - Whether notification is important
 * @param {Function} props.onRemove - Callback to remove notification
 * @returns {JSX.Element} Notification item component
 */
const NotificationItem = ({ notification, onRemove }) => {
  const [isExiting, setIsExiting] = useState(false);
  const [progress, setProgress] = useState(100);
  const progressIntervalRef = useRef();

  // Handle exit animation
  const handleRemove = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      onRemove(notification.id);
    }, 400); // Match the CSS transition duration
  }, [notification.id, onRemove]);

  // Progress bar for timed notifications
  useEffect(() => {
    if (!notification.persist && notification.duration) {
      const duration = notification.duration || 3000;
      const interval = 50; // Update every 50ms
      const decrement = (interval / duration) * 100;
      
      progressIntervalRef.current = setInterval(() => {
        setProgress(prev => {
          const newProgress = prev - decrement;
          if (newProgress <= 0) {
            clearInterval(progressIntervalRef.current);
            handleRemove();
            return 0;
          }
          return newProgress;
        });
      }, interval);

      return () => {
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
        }
      };
    }
  }, [notification.persist, notification.duration, handleRemove]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, []);

  return (
    <div className={`notification-wrapper ${isExiting ? 'exiting' : ''}`}>
      <div className={`custom-notification ${notification.type} ${notification.important ? 'important' : ''} print`}>
        <div className="notification-icon">
          {notification.icon}
        </div>
        <div className="notification-content">
          {notification.title && (
            <div className="notification-title">{notification.title}</div>
          )}
          <div className="notification-message">{notification.message}</div>
        </div>
        
        {notification.type === 'choice' ? (
          <div className="notification-actions">
            <button 
              className="action-button confirm" 
              onClick={notification.onConfirm}
            >
              {notification.confirmText}
            </button>
            <button 
              className="action-button cancel" 
              onClick={notification.onCancel}
            >
              {notification.cancelText}
            </button>
          </div>
        ) : !notification.persist && (
          <button 
            className="notification-close" 
            onClick={handleRemove}
          >
            <FiX />
          </button>
        )}
        
        {/* Progress bar for timed notifications */}
        {!notification.persist && notification.type !== 'choice' && (
          <div 
            className="notification-progress" 
            style={{ width: `${progress}%` }}
          />
        )}
      </div>
    </div>
  );
};

/**
 * Notification Provider component that manages all notification state and provides
 * notification methods to child components.
 *
 * @component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components that will have access to notification context
 * @returns {JSX.Element} Provider component wrapping children with notification context
 *
 * @example
 * ```jsx
 * function App() {
 *   return (
 *     <SnackbarProvider>
 *       <NotifyProvider>
 *         <Router>
 *           <Routes>
 *             <Route path="/dashboard" element={<Dashboard />} />
 *           </Routes>
 *         </Router>
 *       </NotifyProvider>
 *     </SnackbarProvider>
 *   );
 * }
 * ```
 */
export const NotifyProvider = ({ children }) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  // eslint-disable-next-line
  const [connected, setConnected] = useState(true);
  const [customNotifications, setCustomNotifications] = useState([]);
  const [choiceModal, setChoiceModal] = useState(null);
  const notificationIdCounter = useRef(0);
  const [notificationMode, setNotificationMode] = useState('custom'); // 'notistack', 'notiflix', or 'custom'

  /**
   * Removes a custom notification by ID
   * @param {string|number} id - The notification ID to remove
   */
  const removeCustomNotification = useCallback((id) => {
    setCustomNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  /**
   * Adds a new custom notification to the queue
   * @param {Object} notification - The notification object
   * @param {string} notification.type - Notification type
   * @param {string} notification.message - Notification message
   * @param {React.ReactNode} notification.icon - Icon component
   * @param {boolean} [notification.important] - Whether notification is important
   * @param {number} [notification.duration=3000] - Auto-dismiss duration in ms
   * @returns {number} The notification ID
   */
  const addCustomNotification = useCallback((notification) => {
    const id = notificationIdCounter.current++;
    const newNotification = {
      ...notification,
      id,
      duration: notification.duration || 3000
    };

    setCustomNotifications(prev => {
      // Sort notifications: important ones first
      const updated = [newNotification, ...prev];
      return updated.sort((a, b) => {
        if (a.important && !b.important) return -1;
        if (!a.important && b.important) return 1;
        return 0;
      });
    });

    return id;
  }, []);

  const notiflixOptions = useMemo(() => ({
    success: Notiflix.Notify.success,
    failure: Notiflix.Notify.failure,
    info: Notiflix.Notify.info
  }), []);

  const notistackOptions = useMemo(() => ({
    success: (message, ...args) => enqueueSnackbar(message, { variant: 'success', ...args }),
    failure: (message, ...args) => enqueueSnackbar(message, { variant: 'error', ...args }),
    info: (message, ...args) => enqueueSnackbar(message, { variant: 'info', ...args }),
  }), [enqueueSnackbar]);

  const customOptions = useMemo(() => ({
    success: (message, ...args) => {
      return addCustomNotification({
        type: 'success',
        message,
        icon: <FiCheck />,
        ...args
      });
    },
    failure: (message, ...args) => {
      return addCustomNotification({
        type: 'error',
        message,
        icon: <FiAlertTriangle />,
        ...args
      });
    },
    info: (message, ...args) => {
      return addCustomNotification({
        type: 'info',
        message,
        icon: <FiInfo />,
        ...args
      });
    },
  }), [addCustomNotification]);

  const getSelectedLib = useCallback(() => {
    switch (notificationMode) {
      case 'notiflix':
        return notiflixOptions;
      case 'notistack':
        return notistackOptions;
      case 'custom':
      default:
        return customOptions;
    }
  }, [notificationMode, notiflixOptions, notistackOptions, customOptions]);

  const selectedLib = getSelectedLib();

  // Initialize Notiflix when mode is notiflix
  useEffect(() => {
    if (notificationMode === 'notiflix') {
      Notiflix.Notify.init({});
      Notiflix.Report.init({});
      Notiflix.Confirm.init({});
      Notiflix.Loading.init({});
    }
  }, [notificationMode]);

  const extractMessage = useCallback((response) => {
    window.fff = (response)
    const findMessage = (data, _resolve) => {
      if (data.message){
        if(data.message === "Failed to fetch") _resolve('Server disconnected')
        else _resolve(data.message);
      }
      else if (data.error) _resolve(Array.isArray(data.error) ? data.error[0] : data.error);
      else if (data.msg) data.msg === 'Token has expired' ? _resolve(data.msg + ' please refresh') : _resolve(data.msg)
      else if (data.success) _resolve(data.success);
      else _resolve('An unexpected error has occurred');
    };

    return new Promise((resolve) => {
      if (response && response.json) {
        response.json().then((data) => {
          findMessage(data, resolve);
        });
      } else {
        if (response.statusText) resolve(response.statusText);
        else if (response.status) resolve(`Error ${response.status}`);
        else findMessage(response, resolve);
      }
    });
  }, []);

  const handleMessage = useCallback((response, messageType, prefix) => {
    extractMessage(response).then((successMessage) => {
      messageType(successMessage, prefix);
    });
  }, [extractMessage]);

  /**
   * Displays an error notification
   * @param {string|Object} message - Error message or response object
   * @param {string} [prefix] - Optional prefix for the error message
   */
  const error = useCallback((message, prefix) => {
    if (notificationMode !== 'custom') {
      closeSnackbar();
    }

    if (process.env.NODE_ENV === 'development') {
      console.error('An error has occured:', message);
    }

    if (message && typeof message === 'object') {
      handleMessage(message, error, prefix);
    } else {
      selectedLib.failure(`${prefix || 'Error'}: ${message || 'An unexpected error has occurred'}`);
    }
  }, [notificationMode, closeSnackbar, handleMessage, selectedLib]);

  /**
   * Displays a success notification
   * @param {string|Object} message - Success message or response object
   * @param {string} [prefix] - Optional prefix for the success message
   */
  const success = useCallback((message, prefix) => {
    if (message && typeof message === 'object') {
      handleMessage(message, success, prefix);
    } else {
      selectedLib.success(`${prefix || ''} ${message || 'Success!'}`);
    }
  }, [handleMessage, selectedLib]);

  /**
   * Displays a loading spinner notification with completion tracking
   * @param {string} [message='Loading, please wait...'] - Loading message
   * @param {Object} [options={}] - Additional options for the spinner
   * @returns {Object} Spinner control object with complete method
   * @returns {Function} returns.complete - Function to complete the spinner
   *
   * @example
   * ```jsx
   * const loader = spinner('Saving data...');
   * try {
   *   await saveData();
   *   loader.complete(true, 'Data saved successfully!');
   * } catch (error) {
   *   loader.complete(false, 'Failed to save data');
   * }
   * ```
   */
  const spinner = useCallback((message = 'Loading, please wait...', options = {}) => {
    if (notificationMode === 'custom') {
      const id = addCustomNotification({
        type: 'loading',
        message,
        icon: <FiLoader className="spinning-icon" />,
        persist: true,
        ...options
      });

      return {
        complete: (success = true, completionMessage, bypass) => {
          removeCustomNotification(id);
          if (bypass) {
            return;
          }
          if (success) {
            selectedLib.success(completionMessage || 'Completed successfully');
          } else if (completionMessage) {
            selectedLib.failure(completionMessage);
          }
        }
      };
    } else if (notificationMode === 'notistack') {
      const key = enqueueSnackbar(
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <PulseLoader size={6} color="#fff" />
          <span>{message}</span>
        </div>,
        {
          variant: 'info',
          persist: true,
          ...options
        }
      );

      return {
        complete: (success = true, completionMessage, bypass) => {
          closeSnackbar(key);
          if (bypass) {
            return;
          }
          if (success) {
            selectedLib.success(completionMessage || 'Completed successfully');
          } else if (completionMessage) {
            selectedLib.failure(completionMessage);
          }
        }
      };
    } else {
      Notiflix.Loading.circle(message);

      return {
        complete: (success = true, completionMessage, bypass) => {
          Notiflix.Loading.remove();
          if (bypass) {
            return;
          }
          if (success) {
            selectedLib.success(completionMessage || 'Completed successfully');
          } else if (completionMessage) {
            selectedLib.failure(completionMessage);
          }
        }
      };
    }
  }, [notificationMode, addCustomNotification, removeCustomNotification, selectedLib, enqueueSnackbar, closeSnackbar]);

  /**
   * Displays an info notification
   * @param {string|Object} message - Info message or response object
   * @param {string} [prefix] - Optional prefix for the info message
   */
  const info = useCallback((message, prefix) => {
    if (message && typeof message === 'object') {
      handleMessage(message, info, prefix);
    } else {
      selectedLib.info(`${prefix || ''}: ${message || 'Information'}`);
    }
  }, [handleMessage, selectedLib]);

  /**
   * Displays a choice modal for user confirmation
   * @param {string|Object} messageOrOptions - Message string or options object
   * @param {Function} [_success] - Callback for confirm action
   * @param {Function} [_cancel] - Callback for cancel action
   *
   * @example
   * ```jsx
   * // Simple string message
   * choice('Are you sure?', () => deleteItem(), () => console.log('cancelled'));
   *
   * // With options object
   * choice({
   *   title: 'Delete Item',
   *   message: 'This action cannot be undone.',
   *   confirmText: 'Delete',
   *   cancelText: 'Cancel',
   *   onConfirm: () => deleteItem(),
   *   onCancel: () => console.log('cancelled')
   * });
   * ```
   */
  const choice = useCallback((messageOrOptions, _success, _cancel) => {
    let message, options;

    if (typeof messageOrOptions === 'string') {
      message = messageOrOptions;
      options = {
        title: 'Confirmation',
        message,
        confirmText: 'Yes',
        cancelText: 'No'
      };
    } else {
      options = messageOrOptions;
      message = options.message;
    }

    const defaultOptions = {
      title: 'Confirmation',
      confirmText: 'Yes',
      cancelText: 'No',
      onConfirm: _success,
      onCancel: _cancel
    };

    const mergedOptions = { ...defaultOptions, ...options };

    if (notificationMode === 'custom') {
      setChoiceModal({
        title: mergedOptions.title,
        message: mergedOptions.message,
        icon: <FiHelpCircle />,
        confirmText: mergedOptions.confirmText,
        cancelText: mergedOptions.cancelText,
        isLoading: mergedOptions.isLoading,
        loadingText: mergedOptions.loadingText,
        // Success transition options
        showSuccessTransition: mergedOptions.showSuccessTransition,
        successTitle: mergedOptions.successTitle,
        successMessage: mergedOptions.successMessage,
        onConfirm: () => {
          // Don't close modal here if showing success transition - the modal handles it
          if (!mergedOptions.isLoading && !mergedOptions.showSuccessTransition) {
            setChoiceModal(null);
          }
          if (mergedOptions.onConfirm) {
            mergedOptions.onConfirm();
          }
        },
        onCancel: () => {
          if (!mergedOptions.isLoading) {
            setChoiceModal(null);
            if (mergedOptions.onCancel) {
              mergedOptions.onCancel();
            }
          }
        }
      });
    } else if (notificationMode === 'notistack') {
      const choicePrompt = (
        <div className="alert alert-info element-to-hide">
          <p className="mb-0" style={mergedOptions.messageStyle}>
            {message}
          </p>
          <div className="mt-2">
            <button className="btn btn-danger mr-2" type='button' onClick={() => {
              closeSnackbar();
              if (mergedOptions.onConfirm) {
                mergedOptions.onConfirm();
              }
            }}>
              {mergedOptions.confirmText}
            </button>
            <button className="btn btn-secondary" type='button' onClick={() => {
              closeSnackbar();
              if (mergedOptions.onCancel) {
                mergedOptions.onCancel();
              }
            }}>
              {mergedOptions.cancelText}
            </button>
          </div>
        </div>
      );

      enqueueSnackbar(choicePrompt, {
        variant: 'info',
        persist: true,
      });
    } else {
      Notiflix.Confirm.show(
        mergedOptions.title,
        mergedOptions.message,
        mergedOptions.confirmText,
        mergedOptions.cancelText,
        mergedOptions.onConfirm,
        mergedOptions.onCancel
      );
    }
  }, [notificationMode, addCustomNotification, removeCustomNotification, closeSnackbar, enqueueSnackbar]); // eslint-disable-line react-hooks/exhaustive-deps

  const serverDisconnect = useCallback(() => {
    return;
  }, []);

  const setNotificationSystem = useCallback((mode) => {
    if (['notistack', 'notiflix', 'custom'].includes(mode)) {
      setNotificationMode(mode);
    } else {
      if (process.env.NODE_ENV === 'development') {
        console.error(`Invalid notification mode: ${mode}. Must be 'notistack', 'notiflix', or 'custom'`);
      }
    }
  }, []);

  // Custom notifications container component
  const CustomNotificationsContainer = () => {
    if (notificationMode !== 'custom' || customNotifications.length === 0) {
      return null;
    }

    // Filter out choice notifications since they're now handled by the modal
    const nonChoiceNotifications = customNotifications.filter(
      notification => notification.type !== 'choice'
    );

    if (nonChoiceNotifications.length === 0) {
      return null;
    }

    return (
      <div className="custom-notifications-container element-to-hide">
        {nonChoiceNotifications.map(notification => (
          <NotificationItem
            key={notification.id}
            notification={notification}
            onRemove={removeCustomNotification}
          />
        ))}
      </div>
    );
  };

  // Override fetch with better loading states
  const originalFetch = window.fetch;

  window.fetch = async (input, init, spinnerOptions = {}) => {
    if (spinnerOptions.active) {
      const loader = spinner(spinnerOptions.spinnerMessage || 'Loading, please wait...');
      let loaded = false;

      return originalFetch(input, init)
      .then(response => {
        if(!response.ok){
          if(spinnerOptions.bypass){
            loader.complete(false, '', false);
          }else{
            loader.complete(false);
          }
        }else{
          loader.complete(true, spinnerOptions.spinnerSuccess || 'Request completed successfully');
          loaded = true;
        }
        return response;
      }).finally(()=>{
        if(!loaded){
          loader.complete(false);
        }
      })
    }
    return originalFetch(input, init);
  };

  /**
   * The context value object containing all notification methods
   * @typedef {Object} NotificationContextValue
   * @property {Function} error - Display error notifications
   * @property {Function} success - Display success notifications
   * @property {Function} info - Display info notifications
   * @property {Function} spinner - Display loading spinner with completion tracking
   * @property {Function} choice - Display choice modal for confirmations
   * @property {Function} serverDisconnect - Handle server disconnection
   * @property {Function} setNotificationSystem - Change notification system mode
   */
  const contextValue = useMemo(() => ({
    error,
    success,
    spinner,
    choice,
    info,
    serverDisconnect,
    setNotificationSystem
  }), [error, success, spinner, choice, info, serverDisconnect, setNotificationSystem]);

  return (
    <NotifyContext.Provider value={contextValue}>
      {children}
      <CustomNotificationsContainer />
      {choiceModal && notificationMode === 'custom' && (
        <ChoiceModal 
          notification={choiceModal} 
          onClose={() => setChoiceModal(null)}
        />
      )}
    </NotifyContext.Provider>
  );
};